<script lang="ts" setup>
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useCreateProductWithMasterProject } from "@/queries/projects";
import FormAlert from "@/components/FormAlert.vue";

const props = defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  "update:show": [value: boolean];
  created: [data: { productId: string; projectId: string }];
}>();

const router = useRouter();
const { createProductWithMasterProject, createProductWithMasterPending, createProductWithMasterError, createProductWithMasterReset } = useCreateProductWithMasterProject();

const productName = ref("");
const productDescription = ref("");
const clientValidationError = ref("");

// Watch for modal opening to reset form
watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      productName.value = "";
      productDescription.value = "";
      clientValidationError.value = "";
      createProductWithMasterReset();
    }
  }
);

function closeModal() {
  emit("update:show", false);
}

function handleCreate() {
  clientValidationError.value = "";

  if (!productName.value.trim()) {
    clientValidationError.value = "Product name is required";
    return;
  }

  if (!productDescription.value.trim()) {
    clientValidationError.value = "Product description is required";
    return;
  }

  createProductWithMasterProject({
    name: productName.value.trim(),
    description: productDescription.value.trim(),
  }, {
    onSuccess: (result) => {
      emit("created", { productId: result.product.id, projectId: result.project.id });
      closeModal();
      router.push(`/projects/${result.project.id}`);
    },
  });
}
</script>

<template>
  <div
    class="modal fade"
    :class="{ show: show }"
    style="display: block"
    v-if="show"
    @click.self="closeModal"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">Create New Product</h2>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <FormAlert :error="clientValidationError || createProductWithMasterError" @dismiss="() => { clientValidationError = ''; createProductWithMasterReset(); }" />

          <div class="mb-3">
            <p class="text-muted">
              Create a new product with a structured approach. This will:
            </p>
            <ul class="text-muted small">
              <li>Create a new product listing for publishing</li>
              <li>Generate an empty master project to build your workflows</li>
              <li>Set up the foundation for subscriber management</li>
              <li>Enable product configuration and publishing settings</li>
            </ul>
          </div>

          <div class="mb-3">
            <label for="productName" class="form-label"
              >Product Name <span class="text-danger">*</span></label
            >
            <input
              type="text"
              class="form-control"
              id="productName"
              v-model="productName"
              placeholder="Enter product name"
              :disabled="createProductWithMasterPending"
            />
          </div>

          <div class="mb-3">
            <label for="productDescription" class="form-label"
              >Product Description <span class="text-danger">*</span></label
            >
            <textarea
              class="form-control"
              id="productDescription"
              v-model="productDescription"
              rows="3"
              placeholder="Describe what this product does and who it's for"
              :disabled="createProductWithMasterPending"
            ></textarea>
          </div>

          <div class="alert alert-info" role="alert">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Next Steps:</strong> After creation, you'll be taken to the
            master project where you can start building your workflows.
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="closeModal"
            :disabled="createProductWithMasterPending"
          >
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            @click="handleCreate"
            :disabled="createProductWithMasterPending"
          >
            <span
              v-if="createProductWithMasterPending"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            {{ createProductWithMasterPending ? "Creating..." : "Create Product" }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" v-if="show"></div>
</template>
